import { Injectable, NgZone } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { firstValueFrom } from 'rxjs';
import {
    MAPS_UPLOAD_FINALIZE,
    MAPS_UPLOAD_URL_CREATE,
} from './maps-graphql/queries';
import type { MapsUploadDoc, MapsUploadUrlResult } from '../models/maps.types';

const ALLOWED_TYPES: Record<string, string> = {
    txt: 'text/plain',
    pdf: 'application/pdf',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

export const UPLOAD_SIZE_LIMIT_BYTES = 10 * 1024 * 1024;

export function inferContentType(file: File): string | null {
    const ix = file.name.lastIndexOf('.');
    if (ix === -1) return null;
    const ext = file.name.slice(ix + 1).toLowerCase();
    return ALLOWED_TYPES[ext] ?? null;
}

@Injectable({ providedIn: 'root' })
export class MapsUploadService {
    constructor(
        private readonly apollo: Apollo,
        private readonly zone: NgZone,
    ) {}

    /**
     * Three-step upload (TRD §3.4 / §3.5):
     *   1. mapsUploadUrlCreate → uploadId + SAS write URL
     *   2. browser PUT the file directly to Azure
     *   3. mapsUploadFinalize → server extracts text, returns final status
     *
     * On any step's failure, returns a synthetic MapsUploadDoc with
     * extractStatus='extract_failed' so the SPA can render a failed chip
     * without unwinding into separate error paths.
     */
    async upload(file: File): Promise<MapsUploadDoc> {
        const contentType = inferContentType(file);
        if (!contentType) {
            return this.fakeFailedDoc(file, 'unsupported_type');
        }
        if (file.size > UPLOAD_SIZE_LIMIT_BYTES) {
            return this.fakeFailedDoc(file, 'size_exceeded');
        }

        let urlResult: MapsUploadUrlResult;
        try {
            const result = await firstValueFrom(
                this.apollo.mutate<{ mapsUploadUrlCreate: MapsUploadUrlResult }>({
                    mutation: MAPS_UPLOAD_URL_CREATE,
                    variables: {
                        input: {
                            fileName: file.name,
                            contentType,
                            sizeBytes: file.size,
                        },
                    },
                })
            );
            if (!result.data?.mapsUploadUrlCreate) {
                return this.fakeFailedDoc(file, 'upload_register_failed');
            }
            urlResult = result.data.mapsUploadUrlCreate;
        } catch {
            return this.fakeFailedDoc(file, 'upload_register_failed');
        }

        // Direct PUT to Azure — mirrors apps/FeedbackPlatform presentation
        // upload. The SAS URL carries all auth; do NOT include the cookie.
        try {
            const putRes = await fetch(urlResult.uploadUrl, {
                method: 'PUT',
                headers: {
                    'x-ms-blob-type': 'BlockBlob',
                    'Content-Type': contentType,
                },
                body: file,
            });
            if (!putRes.ok) {
                return this.fakeFailedDoc(file, 'blob_put_failed', urlResult.uploadId);
            }
        } catch {
            return this.fakeFailedDoc(file, 'blob_put_failed', urlResult.uploadId);
        }

        try {
            const result = await firstValueFrom(
                this.apollo.mutate<{ mapsUploadFinalize: MapsUploadDoc }>({
                    mutation: MAPS_UPLOAD_FINALIZE,
                    variables: { uploadId: urlResult.uploadId },
                })
            );
            const doc = result.data?.mapsUploadFinalize;
            if (!doc) {
                return this.fakeFailedDoc(file, 'finalize_failed', urlResult.uploadId);
            }
            return doc;
        } catch {
            return this.fakeFailedDoc(file, 'finalize_failed', urlResult.uploadId);
        }
    }

    private fakeFailedDoc(file: File, error: string, uploadId?: string): MapsUploadDoc {
        return {
            uploadId: uploadId ?? `local-${Math.random().toString(36).slice(2, 10)}`,
            originalFileName: file.name,
            contentType: file.type || 'application/octet-stream',
            sizeBytes: file.size,
            extractStatus: 'extract_failed',
            extractedLength: null,
            extractError: error,
        };
    }
}
